import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { automationsApi, type AutomationRule } from '@beacon/api-client';
import type { AutomationUpdateInput } from '@beacon/core/validation';

import { beaconClient } from '@/lib/api/client.js';

export const automationKeys = {
  all: ['automations'] as const,
  list: () => [...automationKeys.all, 'list'] as const,
};

export function useAutomations() {
  return useQuery({
    queryKey: automationKeys.list(),
    queryFn: async (): Promise<AutomationRule[]> => {
      const res = await automationsApi.list(beaconClient);
      return res.automations;
    },
  });
}

interface UpdateVars {
  id: string;
  input: AutomationUpdateInput;
}

export function useUpdateAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateVars) =>
      automationsApi.update(beaconClient, id, input),
    onMutate: async ({ id, input }) => {
      // Optimistic toggle — flip the cached row immediately so the
      // switch animation snaps without a round-trip wait.
      await qc.cancelQueries({ queryKey: automationKeys.list() });
      const previous = qc.getQueryData<AutomationRule[]>(automationKeys.list());
      if (previous) {
        qc.setQueryData<AutomationRule[]>(
          automationKeys.list(),
          previous.map((a) =>
            a.id === id
              ? {
                  ...a,
                  isEnabled:
                    input.isEnabled !== undefined ? input.isEnabled : a.isEnabled,
                }
              : a
          )
        );
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(automationKeys.list(), ctx.previous);
      }
      toast.error("Couldn't update automation", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: automationKeys.list() });
    },
  });
}
